import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    useGetConversationQuery,
    useSendConversationMessageMutation,
} from '../../../redux/api/compassPortal';
import { errMsg } from './bits';

// Bravo — the Compass agent as an OPERATOR. It reads the real project state,
// directs like an MS data scientist, and PROPOSES concrete actions. The human
// approves; the server executes (propose-then-approve, enforced server-side).
// `onActed` lets the parent refresh the tracker after Bravo changes something.
// Messages live in local state seeded from the conversation query — each send
// returns the full updated {messages, proposal}, so there's no cache to sync.
export default function AgentPanel({ projectId, stageId, stageName, onActed }) {
    const { user } = useAuth0();
    const author = user?.name || user?.email || undefined;

    const [messages, setMessages] = useState(null);
    const [proposal, setProposal] = useState(null);   // Bravo's pending action
    const [ownerInput, setOwnerInput] = useState(''); // for proposals needing an owner email
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const scroller = useRef(null);
    const greeted = useRef({});

    const { data: conv, error: convError, refetch } = useGetConversationQuery({ projectId });
    const [sendMessage] = useSendConversationMessageMutation();

    // Seed local messages from the fetched conversation (once per project load).
    useEffect(() => { setMessages(null); setProposal(null); greeted.current = {}; refetch(); /* eslint-disable-next-line */ }, [projectId]);
    useEffect(() => {
        if (conv && messages === null) setMessages(conv.messages || []);
        if (convError && messages === null) { setMessages([]); setError(errMsg(convError) || "Couldn't reach Bravo."); }
        // eslint-disable-next-line
    }, [conv, convError]);

    // Greet a stage once, the first time it's opened with no history for it.
    useEffect(() => {
        if (messages && !busy && !error && !greeted.current[stageId] && !messages.some((m) => m.stage === stageId)) {
            greeted.current[stageId] = true;
            kick('');
        }
        // eslint-disable-next-line
    }, [messages, stageId, error]);
    // clear any pending proposal when switching stages — a proposal raised on
    // one stage must not stay visible/clickable on another.
    useEffect(() => { setProposal(null); }, [stageId]);

    useEffect(() => { scroller.current?.scrollTo(0, scroller.current.scrollHeight); }, [messages, busy, proposal]);

    const apply = (r) => { setMessages(r.messages || []); setProposal(r.proposal || null); setOwnerInput(''); };

    const kick = async (userText) => {
        setBusy(true); setError(null);
        // Show the user's message immediately — the round trip includes Bravo's
        // LLM turn, and a message that only appears with the reply reads as lost.
        const before = messages;
        if (userText) setMessages([...(messages || []), { role: 'user', text: userText, author, stage: stageId }]);
        try { apply(await sendMessage({ projectId, text: userText, stage: stageId, author }).unwrap()); }
        catch (e) {
            setError(errMsg(e) || "Bravo didn't respond. Try again.");
            if (userText) { setMessages(before); setText(userText); }
        }
        setBusy(false);
    };
    const send = async () => {
        const t = text.trim();
        if (!t || busy) return;
        setText('');
        await kick(t);
    };

    // Approve the pending proposal → the server executes it, then Bravo drives on.
    const approve = async () => {
        if (!proposal || busy) return;
        const args = { ...(proposal.args || {}) };
        if (proposal.needsInput === 'owner') {
            if (!ownerInput.trim()) return;
            args.owner = ownerInput.trim();
        }
        setBusy(true); setError(null);
        const p = { action: proposal.action, args, verb: proposal.verb, target: proposal.target };
        setProposal(null);
        try { apply(await sendMessage({ projectId, approve: p, stage: stageId, author }).unwrap()); onActed?.(); }
        catch (e) { setError(errMsg(e) || 'That action failed.'); }
        setBusy(false);
    };
    const skip = async () => { setProposal(null); await kick('Skip that for now — what else?'); };
    const retry = () => { greeted.current[stageId] = true; kick(''); };

    const shown = (messages || []).filter((m) => m.stage === stageId);
    const tierLabel = proposal ? (proposal.tier === 'human' ? 'Your call to commit' : 'Bravo will do this') : '';

    return (
        <aside className="cp-agent">
            <div className="cp-agent-head">
                <span className="cp-agent-dot" />
                <div className="cp-agent-id">
                    <div className="cp-agent-name">Bravo</div>
                    <div className="cp-agent-stage">{stageName || 'Project'} · Data Scientist</div>
                </div>
            </div>
            <div className="cp-agent-log" ref={scroller}>
                {messages === null ? (
                    <div className="cp-agent-note"><span className="cp-spin" /> Loading…</div>
                ) : shown.length === 0 && !busy && !error ? (
                    <div className="cp-agent-note">Bravo is reading the project…</div>
                ) : (
                    shown.map((m, i) => (
                        <div key={i} className={`cp-msg cp-msg--${m.role}`}>
                            {m.role === 'user' && <div className="cp-msg-author">{m.author}</div>}
                            <div className="cp-msg-body">{m.text}</div>
                        </div>
                    ))
                )}
                {busy && <div className="cp-msg cp-msg--agent"><div className="cp-msg-body cp-msg-typing"><span /><span /><span /></div></div>}

                {proposal && !busy && (
                    <div className={`cp-propose ${proposal.tier === 'human' ? 'cp-propose--human' : ''}`}>
                        <div className="cp-propose-tier">{tierLabel}</div>
                        <div className="cp-propose-verb">{proposal.verb}{proposal.target ? ` · ${proposal.target}` : ''}</div>
                        {proposal.why && <div className="cp-propose-why">Why: {proposal.why}</div>}
                        {proposal.needsInput === 'owner' && (
                            <input className="cp-propose-input" value={ownerInput} onChange={(e) => setOwnerInput(e.target.value)}
                                placeholder="owner@charlieoscar.com" />
                        )}
                        <div className="cp-propose-actions">
                            <button className="cp-btn cp-btn--primary cp-btn--sm" onClick={approve}
                                disabled={proposal.needsInput === 'owner' && !ownerInput.trim()}>
                                {proposal.tier === 'human' ? '✓ ' + (proposal.verb || 'Approve') : 'Approve'}
                            </button>
                            <button className="cp-btn cp-btn--ghost cp-btn--sm" onClick={skip}>Skip</button>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="cp-agent-error">
                        <span>{error}</span>
                        <button className="cp-btn cp-btn--sm cp-btn--ghost" onClick={retry}>Retry</button>
                    </div>
                )}
            </div>
            <div className="cp-agent-input">
                <textarea
                    value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Reply to Bravo, or ask a question…" rows={2}
                />
                <button className="cp-btn cp-btn--primary cp-btn--sm" disabled={busy || !text.trim()} onClick={send}>Send</button>
            </div>
        </aside>
    );
}
